import { useEffect, useState } from "react";
import axios from "../api/axios";
import { Alert, Box } from "@mui/material";
import { getActiveRole } from "../utils/auth";

function DatabaseViewer() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [loadingContents, setLoadingContents] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const role = getActiveRole();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (role !== "super_admin") {
      setLoadingTables(false);
      return;
    }

    axios
      .get("/profile/admin/db-tables", { headers })
      .then((res) => {
        if (res.data.success) {
          const list = res.data.tables || [];
          setTables(list);
          if (list.length > 0) {
            setSelectedTable(list[0]);
          }
        } else {
          setError("Failed to fetch database tables");
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Error fetching database tables");
      })
      .finally(() => setLoadingTables(false));
  }, [role]);

  useEffect(() => {
    if (!selectedTable) return;
    setLoadingContents(true);
    setError("");
    axios
      .get(`/profile/admin/db-table-contents/${selectedTable}`, { headers })
      .then((res) => {
        if (res.data.success) {
          setColumns(res.data.columns || []);
          setRows(res.data.rows || []);
        } else {
          setError(`Failed to fetch contents of table ${selectedTable}`);
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || `Error loading table ${selectedTable}`);
      })
      .finally(() => setLoadingContents(false));
  }, [selectedTable]);

  if (role !== "super_admin") {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Access Denied: Only Super Admins are authorized to view database tables.</Alert>
      </Box>
    );
  }

  // Row filtering based on search
  const filteredRows = rows.filter((row) => {
    return Object.values(row).some((val) =>
      String(val || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900/10 p-4 md:p-6 transition-all duration-300 font-sans">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Block */}
        <div className="bg-white dark:bg-slate-800/40 rounded-2xl shadow-sm border border-slate-200/60 dark:border-slate-700 p-6 flex items-center gap-3">
          <span className="text-3xl">🗄️</span>
          <div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 tracking-tight font-sans">
              Database Inspector
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-sans">
              Super Admin Desk: Read-only live view of all relational tables and schema declarations.
            </p>
          </div>
        </div>

        {error && (
          <Alert severity="error" className="rounded-xl">
            {error}
          </Alert>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Tables List */}
          <div className="w-full lg:w-72 flex-shrink-0">
            <div className="bg-white dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700 rounded-2xl shadow-sm p-4 space-y-3">
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-300 uppercase tracking-wider px-2 font-sans">
                Tables List ({tables.length})
              </h2>
              {loadingTables ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-xs text-slate-400 mt-2 font-sans italic">Loading tables...</p>
                </div>
              ) : (
                <div className="flex flex-row lg:flex-col overflow-x-auto lg:overflow-x-visible gap-1 pb-2 lg:pb-0 max-h-[500px] custom-scrollbar lg:pr-1">
                  {tables.map((tbl) => (
                    <button
                      key={tbl}
                      onClick={() => {
                        setSelectedTable(tbl);
                        setSearchTerm("");
                      }}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-semibold font-mono tracking-wide transition-all duration-200 whitespace-nowrap lg:whitespace-normal ${
                        selectedTable === tbl
                          ? "bg-blue-600 text-white shadow-md shadow-blue-500/20 translate-x-1"
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/40"
                      }`}
                    >
                      📁 {tbl}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Table Contents Section */}
          <div className="flex-1 min-w-0">
            <div className="bg-white dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700 rounded-2xl shadow-sm p-6 space-y-6">
              
              {/* Table Info Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-200/60 dark:border-slate-700 pb-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 font-mono">
                    Table: {selectedTable || "None Selected"}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-sans">
                    Columns: {columns.length} | Rows: {rows.length}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search columns and rows..."
                    className="w-full md:w-72 px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/60 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-sans"
                    value={searchTerm}
                    disabled={!selectedTable || loadingContents}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {loadingContents ? (
                <div className="text-center py-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-sm text-slate-400 mt-4 italic font-sans">Loading data from {selectedTable}...</p>
                </div>
              ) : !selectedTable ? (
                <div className="text-center py-12 text-slate-400 italic font-sans">
                  Select a table from the sidebar list to view its schema and content rows.
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Columns Schema Section */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-slate-800 dark:text-slate-300 uppercase tracking-wider font-sans">
                      📐 Table Schema Structure
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {columns.map((col) => (
                        <div
                          key={col.name}
                          className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 flex flex-col hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                        >
                          <span className="text-xs font-mono font-bold text-slate-800 dark:text-slate-100">
                            {col.name}
                            {col.key === "PRI" && <span className="text-amber-500 ml-1">🔑</span>}
                          </span>
                          <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500 uppercase mt-0.5">
                            {col.type} {col.nullable ? "(Null)" : "(Not Null)"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rows Data Table */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-slate-800 dark:text-slate-300 uppercase tracking-wider font-sans">
                      📝 Live Records Rows
                    </h4>
                    {filteredRows.length === 0 ? (
                      <div className="bg-slate-50/60 dark:bg-slate-900/10 border border-slate-200/60 dark:border-slate-700 rounded-xl p-8 text-center italic text-xs text-slate-400 font-sans">
                        No rows recorded or matching search term in this table.
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900/10">
                        <table className="w-full border-collapse text-left text-xs text-slate-700 dark:text-slate-300">
                          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-800 dark:text-slate-200 font-semibold border-b border-slate-200 dark:border-slate-700 font-sans">
                            <tr>
                              {columns.map((col) => (
                                <th key={col.name} className="px-4 py-3 font-mono font-semibold">
                                  {col.name}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60 font-mono">
                            {filteredRows.map((row, idx) => (
                              <tr
                                key={idx}
                                className="hover:bg-slate-50/50 dark:hover:bg-slate-800/10 transition-colors"
                              >
                                {columns.map((col) => {
                                  const cellValue = row[col.name];
                                  return (
                                    <td key={col.name} className="px-4 py-3 truncate max-w-xs">
                                      {cellValue === null ? (
                                        <span className="text-slate-400 dark:text-slate-600 italic">NULL</span>
                                      ) : typeof cellValue === "boolean" ? (
                                        cellValue ? "TRUE" : "FALSE"
                                      ) : (
                                        String(cellValue)
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                </div>
              )}

            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default DatabaseViewer;
